import { type Except, notEmpty, type ValidatedKey } from '@festina-life/core'
import { useField } from 'formik'

import { PercentageInput } from '../../forms/percentageInput/PercentageInput'
import type { RawPercentageInputProps } from '../../forms/percentageInput/RawPercentageInput'
import { useFormikErrorWithState } from '../../hooks/useFormikErrorWithState'
import { useFormikIsDisabled } from '../../hooks/useFormikIsDisabled'
import { useFormikIsRequired } from '../../hooks/useFormikIsRequired'
import { usePercentageFormatter } from '../../hooks/usePercentageFormatter'
import { useExistingValuesContext } from '../../theme/existingValuesContext'
import {
  handleDefaultValueClick,
  handleExistingValueClick,
  handleOnBlur,
} from '../formikUtils/formikUtils'
import { getWrapperProps } from '../formikUtils/getWrapperProps'
import { useNumericValueDifferenceCheck } from '../formikUtils/useNumericValueDifferenceCheck'
import { withFormikInputDiffing } from '../formikUtils/withFormikInputDiffing'
import type { FormikInputPropsForWrapper } from '../types'

export type FormikPercentageInputProps = Except<
  RawPercentageInputProps,
  'defaultValue' | 'onBlur' | 'onChange'
> & {
  name: ValidatedKey
  defaultValue?: number
  onBlur?: (value: number | undefined) => void
} & FormikInputPropsForWrapper

const FormikPercentageInputInternal = ({
  name,
  label,
  isRequired,
  defaultValue,
  validationText,
  onDefaultValueClick,
  hasRightColumnAlways,
  description,
  disabled,
  onBlur,
  className,
  existingValueConfig,
  ...restProps
}: FormikPercentageInputProps) => {
  const { formatPercentWithDecimals } = usePercentageFormatter()

  const [field, meta, { setValue }] = useField(name)
  const { isInError, error } = useFormikErrorWithState(meta)
  const isFieldDisabled = useFormikIsDisabled(disabled)

  const existingValue = useExistingValuesContext()?.getExistingValue?.(name) as
    | number
    | undefined

  const inputValueDifferenceFromExistingValue =
    useNumericValueDifferenceCheck(name)

  const wrapperProps = getWrapperProps({
    label,
    existingValue:
      typeof existingValue === 'number'
        ? formatPercentWithDecimals(existingValue)
        : undefined,
    existingValueConfig,
    inputValueDifferenceFromExistingValue,
    commonWrapperProps: {
      isRequired: useFormikIsRequired(name, isRequired),
      defaultValue: notEmpty(defaultValue)
        ? formatPercentWithDecimals(defaultValue)
        : undefined,
      validationText,
      description,
      onDefaultValueClick: () =>
        handleDefaultValueClick({
          defaultValue,
          onBlur,
          onDefaultValueClick,
        }),
      onExistingValueClick: () =>
        handleExistingValueClick({
          existingValue,
          setValue,
          onBlur,
        }),
      htmlFor: name,
      error,
      hasRightColumnAlways,
      className,
    },
  })

  return (
    <PercentageInput
      wrapperProps={wrapperProps}
      id={name}
      placeholder={existingValue ?? defaultValue}
      isInError={isInError}
      disabled={isFieldDisabled}
      {...field}
      {...restProps}
      onChange={setValue}
      onBlur={(event) => handleOnBlur({ event, field, onBlur })}
    />
  )
}

export const FormikPercentageInput = withFormikInputDiffing(
  FormikPercentageInputInternal,
)
